/* eslint-disable consistent-return */
import useCommon from '@utils/hooks/common';
import {cloneDeep} from 'lodash-es';
import Poker from '@components/Poker';
import classNames from 'classnames';
import dayjs from 'dayjs';
import {
  AbsolutePosition,
  BestCombination,
  GameResultOnScreen,
  PlayerComponent as PlayerComponentInterface,
  PokerComponent,
  TableArea,
} from '@interface';
import {notification, Spin, Statistic} from 'antd';
import {
  CHIP_TRANSITION_DURATION,
  COLOR_THEME,
  GAME_MODE,
  HAND_TYPE,
  PLAYER_LAYOUT,
  RESULT_TYPE,
  SUIT,
} from '@constant/game';
import React, {
  useState,
  useEffect,
  createElement,
  useRef,
  useImperativeHandle,
  useContext,
  useCallback,
  useMemo,
} from 'react';
import {
  debounce,
  getBestCombinationText,
  getCumulativeOffset,
  getPokerImage,
  getSafe,
} from '@utils/index';
import {useDebugView, useRuntimeEnv} from '@utils/hooks/debug';
import MovableObject from '@components/MovableObject';
import styles from './styles';
import PlayerComponent from '../Player';
import EmptySeat from '../EmptySeat/canvas';
import RegularTable from './RegularTable';
import AllInTable from './AllInTable';
import ChipsImage from '@components/ChipsImage';
import MultiUnitStatistic from '../MultiUnitStatistic';
import {GameLogicInterface} from 'poker-interfaces';
import {TABLET_BREAKPOINT} from '@constant/system';
import {EmojiContext, PositionContext} from '@utils/context';
import ResultImage from '../ResultImage';
import {useWhyDidYouUpdate} from 'ahooks';
import {get, isNil} from 'lodash-es';

const MAX_CHIP_COUNT = 6;

/**
 * @param {number} waitingTime - If === -1 --> waiting others. if > 0 --> countdown seconds
 */

interface MovableObjectInterface {
  children?: JSX.Element;
  destination?: AbsolutePosition;
  containerElement?: HTMLElement;
  getAbsolutePosition?: () => AbsolutePosition;
  unhide?: any;
  moveToDestinationWithFade?: any;
  moveToPot?: any;
}

const TableAreaMobile = React.forwardRef((props: TableArea, ref) => {
  const {t, classes, socket, system, theme, user} = useCommon({
    withStyles: styles,
    withTranslation: ['table_area', 'best_combination', 'common'],
    observers: ['game', 'socket', 'system', 'user'],
  });
  const playersRef = useRef<Array<PlayerComponentInterface>>([]);
  const movableObjRef = useRef<Array<MovableObjectInterface>>([]);
  const centerContainerRef = useRef<HTMLDivElement>();
  const chipCollectionPointRef = useRef<HTMLDivElement>();
  const regularTableRef = useRef(null);

  const isDebugView = useDebugView();

  const getMyPlayerId = () => get(user, 'playerInfo.id', null);
  const myPlayerId = getMyPlayerId();

  const {isLocal} = useRuntimeEnv();
  const {isGameOverlayShown} = system;

  const isMobile = getSafe(() => window.innerWidth < TABLET_BREAKPOINT, false);
  const isIphone7 = getSafe(
    () => window.screen.height === 667 && window.screen.width === 375,
    false
  );
  const isIphone12 = getSafe(
    () => window.screen.height === 844 && window.screen.width === 390,
    false
  );
  const isIphone13Mini = getSafe(
    () => window.screen.height === 812 && window.screen.width === 375,
    false
  );
  const isGalaxyS22 = getSafe(
    () => window.screen.height === 780 && window.screen.width === 360,
    false
  );

  const {
    scaleUpValue,
    componentsPositions,
    playerIconCenterPositions,
    playerChipPlacementPosition,
    updateComponentPosition,
    updateScaleUpValue,
  } = useContext(PositionContext);

  // const [sliderExpanded, setSliderExpanded] = useState(false);
  const {
    tableIndex,
    communityHands,
    outCards,
    playerInActionOrder: playerInAction,
    players,
    pot,
    prevPot,
    gameResultOnScreen,
    gameMode,
    isAllIn,
    roomCode,
    buyInCallback,
    waitingTime,
    actionTime,
    areActionsDisabled,
    insuranceInfo,
    shouldCardsAnimationBePlayed,
    dealerButtonPositionFromGameRoom,
    myHands,
    myBestCombination,
    roomType,
    isCurrentTableInView,
  } = props;

  // useWhyDidYouUpdate('TableAreaMobile', {...props});

  const [isSeated, setIsSeated] = useState<boolean>(false);
  const [actionTimerStatus, setActionTimerStatus] = useState<{
    order: number;
    expiredAt: dayjs.Dayjs;
    duration: number;
  }>(null);
  const [tableMessage, setTableMessage] = useState<JSX.Element | string>();
  const [tableChipsValue, setTableChipsValue] = useState<Array<number>>(
    Array(MAX_CHIP_COUNT).fill(0)
  );
  const [tableAreaTopPosition, setTableAreaTopPosition] = useState<number>(0);
  const [dealerButtonIndex, setDealerButtonIndex] = useState<number>(0); // After "seat rotation"

  const playerUiSequence = useMemo((): PlayerComponentInterface[] => {
    const getMyPlayerId = () => get(user, 'playerInfo.id', null);

    const myPlayerIndexInArray = players.findIndex(
      player => player && player?.id === getMyPlayerId()
    );

    if (myPlayerIndexInArray < 0) {
      // Observer mode
      // Place first empty seat closest to the user
      const emptySeatIndex = players.findIndex(player => !player?.id);
      if (emptySeatIndex <= 0) {
        return players;
      }

      const copy = players.map(player => player);
      // Place empty seat at the beginning of array
      const allPlayersBeforeMe = copy.splice(0, emptySeatIndex);
      copy.push(...allPlayersBeforeMe);
      return copy;
    }

    const copy = players.map(player => player);
    // Place myPlayer at the beginning of array
    const allPlayersBeforeMe = copy.splice(0, myPlayerIndexInArray);
    copy.push(...allPlayersBeforeMe);
    return copy;
  }, [players, user]);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    const index = findDealerButtonIndex(dealerButtonPositionFromGameRoom);
    setDealerButtonIndex(index);
  }, [playerUiSequence, dealerButtonPositionFromGameRoom]);

  const isDesktop = theme?.currentStyleFormat?.id === 'desktop';

  const getPotChipsPositions = (): void => {
    const collectPointRef = chipCollectionPointRef?.current;

    if (collectPointRef) {
      const absolutePosition = getCumulativeOffset(collectPointRef);

      if (absolutePosition.width > 0 && absolutePosition.height > 0) {
        updateComponentPosition(
          'chips',
          absolutePosition.top,
          absolutePosition.right,
          absolutePosition.bottom,
          absolutePosition.left
        );
      }
    }
  };

  useEffect(() => {
    if (actionTimerStatus) {
      const {order, expiredAt, duration} = actionTimerStatus;
      const uiSequence = playerUiSequence;
      const uiIndex = uiSequence.findIndex(player => player && player.order === order);

      playersRef.current[uiIndex]?.resetTimer({expiredAt, duration});
    }
  }, [actionTimerStatus]);

  useEffect(() => {
    const refs = playersRef?.current;
    if (refs && refs?.length > 0) {
      const filterList = players.filter(a => a);
      if (filterList.length > 1) {
        setTableMessage(null);
      } else {
        let message: string | JSX.Element = null;
        message = t('waiting_for_other_players');
        setTableMessage(message);
      }

      const state = filterList?.some(({id}) => id === getMyPlayerId());
      setIsSeated(state);
    }
  }, [players]);

  useEffect(() => {
    setTimeout(() => {
      // Let DOM update before re-calculation
      getPotChipsPositions();
    }, 500);

    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const handleResizeEnd = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      timeoutId = setTimeout(() => {
        getPotChipsPositions();
      }, 500); // adjust delay as needed
    };

    window.addEventListener('resize', handleResizeEnd);
    return () => {
      window.removeEventListener('resize', handleResizeEnd);
    };
  }, [isDesktop, scaleUpValue, isCurrentTableInView]);

  useEffect(() => {
    tableChipsValue.forEach((chipValue, loopIndex) => {
      if (chipValue > 0 && movableObjRef?.current?.[loopIndex]) {
        movableObjRef.current[loopIndex].unhide();
      }
    });
  }, [tableChipsValue]);

  useEffect(() => {
    let message: string | JSX.Element = null;
    if (waitingTime === -1) {
      message = t('waiting_for_other_players');
    } else if (waitingTime > 0) {
      message = t('waiting_for_game_start');
    }
    setTableMessage(message);
  }, [waitingTime]);

  const getWinnerPosition = (winnerIds: number[]): number[] => {
    // if (isDebugView) {
    //   return [0, 1, 2, 3, 4, 5];
    // }

    const playerSequence = playerUiSequence;
    const indices = playerSequence.reduce<number[]>((prev, current, currentIndex) => {
      if (winnerIds.includes(current?.id)) {
        return [...prev, currentIndex];
      }
      return prev;
    }, []);
    return indices;
  };

  useEffect(() => {
    if (isDebugView) {
      if (gameResultOnScreen?.potWinnerIds?.length > 0) {
        const winnerIndices = getWinnerPosition(gameResultOnScreen.potWinnerIds);
        setTableChipsValue(
          Array(MAX_CHIP_COUNT)
            .fill(0)
            .map((_v, index) =>
              winnerIndices.includes(index) ? gameResultOnScreen.winAmountForPlayer : 0
            )
        );
      } else {
        setTableChipsValue(
          Array(MAX_CHIP_COUNT)
            .fill(0)
            .map((_v, index) => 23)
        );
      }
      return;
    }
    if (!gameResultOnScreen && (!prevPot || prevPot <= 0)) {
      setTableChipsValue(Array(MAX_CHIP_COUNT).fill(0));
    } else if (!gameResultOnScreen && prevPot > 0) {
      setTableChipsValue(
        Array(MAX_CHIP_COUNT)
          .fill(0)
          .map((_v, index) => (index === 0 ? prevPot : 0))
      );
    } else if (gameResultOnScreen?.potWinnerIds?.length > 0) {
      const winnerIndices = getWinnerPosition(gameResultOnScreen.potWinnerIds);
      setTableChipsValue(
        Array(MAX_CHIP_COUNT)
          .fill(0)
          .map((_v, index) =>
            winnerIndices.includes(index) ? gameResultOnScreen.winAmountForPlayer : 0
          )
      );
    } else {
      setTableChipsValue(Array(MAX_CHIP_COUNT).fill(0));
    }
  }, [prevPot, gameResultOnScreen]);

  const findDealerButtonIndex = (dealerPosition: number): number => {
    if (dealerPosition < 0 || dealerPosition === null || dealerPosition === undefined) return;

    const maxPlayerLength = isAllIn ? 4 : 6;

    if (!isMyselfSeated) {
      const emptySeatIndex = players.findIndex(player => !player?.id);
      if (emptySeatIndex <= 0 || isNil(emptySeatIndex)) {
        return dealerPosition;
      }

      const clockwiseRotationCount = maxPlayerLength - emptySeatIndex;
      return (clockwiseRotationCount + dealerPosition) % maxPlayerLength;
    }

    const myIndex = players.findIndex(player => player?.id === myPlayerId);

    const dealerPositionIfMyselfIsSeated =
      (maxPlayerLength - myIndex + dealerPosition) % maxPlayerLength;

    // maxPlayerLength - myIndex is the distance from myPlayer to an empty seat.
    // i.e. how many times to rotate clockwise to make myPlayer to the empty seat.
    // +dealerPosition to rotate the button clockwise the same amount of times

    return dealerPositionIfMyselfIsSeated;
  };

  const renderChipSection = (): JSX.Element => {
    const chips = [0, 1, 2, 3, 4, 5].map(playerIndex => (
      <div className={classes.potChip} key={playerIndex}>
        <MovableObject
          ref={el => {
            if (movableObjRef?.current) {
              movableObjRef.current[playerIndex] = el;
            }
          }}
        >
          {tableChipsValue[playerIndex] > 0 ? (
            <div className={classes.chipsWrapper}>
              <ChipsImage chipAmount={tableChipsValue[playerIndex]} />
              <div className={classes.potChipValue}>
                <MultiUnitStatistic
                  valueStyle={{color: 'white', fontSize: isDesktop ? '8px' : '12px'}}
                  value={tableChipsValue[playerIndex]}
                />
              </div>
            </div>
          ) : null}
        </MovableObject>
      </div>
    ));

    return (
      <div className={isAllIn ? classes.chipSectionContainerAllIn : classes.chipSectionContainer}>
        <div
          style={{
            position: 'absolute',
            height: 40,
            width: 40,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {chips}
        </div>
        <div
          style={{
            position: 'absolute',
            height: 40,
            width: 40,
            backgroundColor: isDebugView ? 'rgba(30, 30, 30, 0.5)' : 'transparent',
          }}
          ref={chipCollectionPointRef}
        />
      </div>
    );
  };

  useEffect(() => {
    if (centerContainerRef.current) {
      const handleWindowResize = () => {
        const tableAreaPosition = centerContainerRef.current?.getBoundingClientRect();
        const {top, right, bottom, left} = tableAreaPosition || {};
        // for debug:
        // console.log(' @@@ updateComponentPosition @@@ ', left, top);
        updateComponentPosition('tableArea', top, right, bottom, left);
        setTableAreaTopPosition(top);
      };
      // handleWindowResize();
      const debounceResize = debounce(handleWindowResize, 500);
      // avoid issues with incorrect values being returned while the page is still loading.
      debounceResize();
      window.addEventListener('resize', debounceResize);
      return () => window.removeEventListener('resize', debounceResize);
    }
  }, [scaleUpValue, isGameOverlayShown]);

  const getScaleUpValueBasedOnScreenSize = () => {
    const isMobile = getSafe(() => window.innerWidth < TABLET_BREAKPOINT, false);

    if (isMobile) {
      //! table img width === 437, * 0.9 coz the table border wanna be hidden
      let scaleValue = window.innerWidth / (437 * 0.9);
      if (scaleValue > 1.25) {
        scaleValue = 1.25;
      } else if (scaleValue < 1) {
        scaleValue = 1;
      }

      if (isIphone7) {
        console.log(' *** isIphone7 *** ');
        scaleValue = 0.82;
      } else if (isIphone12) {
        console.log(' !!! isIphone12 !!! ');
        scaleValue = 1.01;
      } else if (isIphone13Mini) {
        console.log(' !!! isIphone13Mini !!! ');
        scaleValue = 0.97;
      } else if (isGalaxyS22) {
        console.log(' !!! isGalaxyS22 !!! ');
        scaleValue = 0.93;
      }

      return scaleValue;
    } else {
      const scaleValue = window.innerWidth / TABLET_BREAKPOINT;
      //!  give space to upper control bar and action buttons
      const maxHeight = window.innerHeight * 0.75;
      const tableHeight = centerContainerRef.current.offsetHeight;
      const tableImageWidth = 437;

      if (tableHeight && tableHeight * scaleValue > maxHeight) {
        return maxHeight / tableHeight;
      } else {
        //! in case tableHeight is 0/undefined
        const maxScaleUpValue = maxHeight / tableImageWidth;
        return scaleValue > maxScaleUpValue ? maxScaleUpValue : scaleValue;
      }
    }
  };

  useEffect(() => {
    // const handleResize = () => {
    //   console.log('resize');
    //   updateScaleUpValue(getScaleUpValueBasedOnScreenSize());
    // };
    // const debouncedHandleResize = debounce(handleResize, 1000);
    // debouncedHandleResize();
    // window.addEventListener('resize', debouncedHandleResize);
    // return () => window.removeEventListener('resize', debouncedHandleResize);
  }, []);

  const isPortrait = window && window.innerHeight > window.innerWidth;
  // adjustment when table blocks upper buttons
  // TODO seems buggy
  useEffect(() => {
    const tableHeight = centerContainerRef.current.offsetHeight;
    const upperControlPanelHeight = 50;
    const actionButtonsHeight = 50;

    const scaleValueBasedOnScreenSize = getScaleUpValueBasedOnScreenSize();

    // adjustedScalingValue would be infinity if tableHeight === 0
    if (isIphone7 || !isMobile || tableHeight === 0 || !isPortrait) {
      updateScaleUpValue(scaleValueBasedOnScreenSize);
    } else if (tableAreaTopPosition <= upperControlPanelHeight * 1.25) {
      // console.log(' *** tableAreaTopPosition is less than 62.5 *** ');
      const adjustedScalingValue =
        (window.innerHeight - (actionButtonsHeight + upperControlPanelHeight) * 1.5) / tableHeight;

      updateScaleUpValue(Math.min(adjustedScalingValue, scaleValueBasedOnScreenSize));
    }
  }, [tableAreaTopPosition, isPortrait]);

  const isDestinationInScreen = (destination: AbsolutePosition) => {
    return (
      destination.left >= 0 &&
      destination.top >= 0 &&
      destination.left <= window.innerWidth &&
      destination.top <= window.innerHeight
    );
  };

  useImperativeHandle(
    ref,
    () => ({
      moveChipsToPlayers: (playerIds: Array<number>) => {
        // console.log('moveChipsToPlayers', playerIds);

        const playerIndices = getWinnerPosition(playerIds);
        // console.log('moveChipsToPlayers Regular table', playerIds, playerIndices);
        playerIndices.forEach(async playerIndex => {
          // console.log('moveChipsToPlayers', playerIndex, dest);
          // console.log('dest', dest);
          movableObjRef.current[playerIndex].unhide();

          const firstPos = playerChipPlacementPosition?.[playerIndex];
          const secondPos = playerIconCenterPositions?.[playerIndex];

          console.log(
            'firstPos',
            firstPos,
            'secondPos',
            secondPos,
            'isDestinationInScreen',
            isDestinationInScreen(firstPos),
            isDestinationInScreen(secondPos)
          );

          if (
            firstPos &&
            secondPos &&
            isDestinationInScreen(firstPos) &&
            isDestinationInScreen(secondPos)
            // Prevent moving chips out of screen when switching tables as position calculation needs time
          ) {
            // Positions may be null in all in table as [4] & [5] are empty
            movableObjRef.current[playerIndex].moveToWithoutReset({
              left: firstPos.xCenter,
              top: firstPos.yCenter,
            });

            await new Promise(resolve => {
              setTimeout(() => {
                resolve(null);
              }, 1000);
            });

            movableObjRef.current[playerIndex].moveToDestinationWithFade({
              top: secondPos.yCenter,
              left: secondPos.xCenter,
            });
          }
        });
      },
      collectAllChipsToPot: async () => {
        if (isDestinationInScreen(componentsPositions?.chips)) {
          return regularTableRef?.current?.collectAllChipsToPot();
        }
        return Promise.resolve(null);
      },
      resetPlayerTimer: (playerOrder: number, expiredAt: dayjs.Dayjs, duration: number) => {
        regularTableRef?.current?.resetPlayerTimer(playerOrder, expiredAt, duration);
      },
      unhidePlayerTimer: () => {
        regularTableRef?.current?.unhidePlayerTimer();
      },
      hidePlayerTimer: () => {
        regularTableRef?.current?.hidePlayerTimer();
      },
      distributeCards: () => {
        regularTableRef?.current?.distributeCards();
      },
    }),
    [
      movableObjRef,
      players,
      regularTableRef,
      playersRef,
      playerUiSequence,
      playerChipPlacementPosition,
      actionTimerStatus?.order,
      componentsPositions,
      playerIconCenterPositions,
    ]
  );

  const renderCommunityHands = useCallback(
    (): Array<JSX.Element> =>
      communityHands?.map(({suit, rank, state}, index) => (
        // TODO
        <Poker
          shouldCardAnimationBePlayed={shouldCardsAnimationBePlayed}
          key={suit + rank + index}
          state={state}
          // set type
          isCommunityCard
          isInitiallyFlipped
          cardIndex={index}
          suit={suit}
          rank={rank}
          cardSize={isDesktop ? 63 : 64}
        />
      )),
    [communityHands, isDesktop, shouldCardsAnimationBePlayed]
  );

  const renderOutCards = useCallback(
    (): Array<JSX.Element> =>
      outCards?.map(({suit, rank, state}, index) => (
        <Poker
          key={suit + rank + index}
          state={state}
          suit={suit}
          rank={rank}
          cardSize="extraSmall"
        />
      )),
    [outCards]
  );

  const isMyselfSeated = players.some(player => player?.id === myPlayerId);

  const renderGameResult = ({winner, potName, prize}): JSX.Element => {
    if (!gameResultOnScreen) return null;

    return (
      <section className={classes.debugResultContainer}>
        {isLocal ? (
          <>
            <div className={classes.gameResultRow}>{`Winner: ${winner}`}</div>
            {potName ? <div className={classes.gameResultRow}>{`pot: ${potName}`}</div> : null}
            <div className={classes.gameResultRow}>{`Prize: ${prize}`}</div>
          </>
        ) : null}
      </section>
    );
  };

  const renderTableMessage = () => {
    return (
      <div className={isAllIn ? classes.tableMessageAllIn : classes.tableMessage}>
        {tableMessage}
      </div>
    );
  };

  let winner;
  let combinationName;
  let bestCombination: BestCombination;
  let prize;
  let potName;
  if (gameResultOnScreen) {
    winner = gameResultOnScreen?.potWinnerIds;
    combinationName = gameResultOnScreen?.combinationName;
    bestCombination = {
      cards: gameResultOnScreen.selectedCards,
      combinationName: gameResultOnScreen.combinationName,
      displayValue: gameResultOnScreen.cardValues,
    };
    prize = gameResultOnScreen?.winAmountForPlayer;
    potName = gameResultOnScreen?.potSequence;
  }

  return (
    <section
      id={roomCode && isSeated ? `table_${tableIndex}` : `observerTable_${tableIndex}`}
      ref={centerContainerRef}
      className={classes.centerContainer}
      style={{transform: `scale(${scaleUpValue})`}}
    >
      {renderChipSection()}
      {!isAllIn ? (
        <RegularTable
          tableIndex={tableIndex}
          gameMode={gameMode}
          roomType={roomType}
          pot={pot}
          isCurrentTableInView={isCurrentTableInView}
          playerSequence={playerUiSequence}
          renderCommunityHands={renderCommunityHands}
          renderOutCards={renderOutCards}
          dealerBoxPosition={dealerButtonIndex}
          isAllInMode={isAllIn}
          myHands={myHands}
          myBestCombination={myBestCombination}
          isTimerHidden={areActionsDisabled}
          playerInAction={playerInAction}
          gameResultOnScreen={gameResultOnScreen}
          actionTime={actionTime}
          buyInCallback={buyInCallback}
          isSeated={isSeated}
          ref={regularTableRef}
          setActionTimerStatus={setActionTimerStatus}
          actionTimerStatusOrder={actionTimerStatus?.order}
          isMyselfSeated={isMyselfSeated}
        />
      ) : (
        <AllInTable
          tableIndex={tableIndex}
          gameMode={gameMode}
          roomType={roomType}
          pot={pot}
          isCurrentTableInView={isCurrentTableInView}
          playerSequence={playerUiSequence}
          renderCommunityHands={renderCommunityHands}
          renderOutCards={renderOutCards}
          dealerBoxPosition={dealerButtonIndex}
          isAllInMode={isAllIn}
          myHands={myHands}
          myBestCombination={myBestCombination}
          isTimerHidden={areActionsDisabled}
          playerInAction={playerInAction}
          gameResultOnScreen={gameResultOnScreen}
          actionTime={actionTime}
          buyInCallback={buyInCallback}
          isSeated={isSeated}
          ref={regularTableRef}
          setActionTimerStatus={setActionTimerStatus}
          actionTimerStatusOrder={actionTimerStatus?.order}
          isMyselfSeated={isMyselfSeated}
        />
      )}
      {gameResultOnScreen ? (
        <section
          className={isAllIn ? classes.gameResultContainerAllIn : classes.gameResultContainer}
        >
          {/* // TODO Use React.memo to map */}
          <div className={classes.gameResultContainerRelativeDiv}>
            <ResultImage combinationName={gameResultOnScreen?.combinationName} />
          </div>
          {bestCombination?.combinationName ? (
            <div className={classes.combinationNameGradient}>
              {getBestCombinationText(t, bestCombination)}
            </div>
          ) : null}
        </section>
      ) : null}
      {gameResultOnScreen ? renderGameResult({winner, potName, prize}) : null}
      {tableMessage ? renderTableMessage() : null}
    </section>
  );
});

TableAreaMobile.displayName = 'TableAreaMobile';

export default React.memo(TableAreaMobile);
